/* eslint no-unused-vars: "off" */
import React, { useContext, useRef, useState } from 'react';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavigateToWindowWithNativeEvents, sendNativeMessage, NativeMessageEvent } from '@components/_common/link/link';
import { useIntersectionObserver } from 'usehooks-ts';
import { isCarvanaNativeApp } from '@carvana/core-native-messaging';
import { datadogRum } from '@datadog/browser-rum';
import Card from '../_common/card/Card';
import MoneyBackBadge from '../_common/moneyback-badge/MoneyBackBadge';
import MoneyBackProgressBar from '../_common/moneyback-progress-bar/MoneyBackProgressBar';
import { DateMasks, AnalyticEvents, AppRoute } from '../../constants';
import {
  SevenDayGuaranteeCardWrapper,
  ContentWrapper,
  Content,
  Button,
  BoldTitle,
  ProgressBarAndBadgeWrapper,
  TextWrapper,
  InfoText,
  BoldText,
  AdditionalInfoText,
  CustomLink,
} from './SevenDayGuaranteeCard.styles';
import { useInViewport } from '../../utils/CustomHooks';
import { formatDateMST } from '../../services/UtilitiesService';
import MoneyBackIcon from '../../assets/MoneyBackIcon';

const SevenDayGuaranteeCard = ({ feedData, positionValue }: any) => {
  const { trackEvent } = useContext(AnalyticsContext) as any;
  const [cardPosition] = useState(positionValue || 0);
  const progressBarBadgeRef: any = useRef(null);
  const {
    id: cardId,
    title,
    content: {
      sevenDayGuarantee: { expirationDate, daysLeft, expired },
    },
  } = feedData;

  const buttonText = '7-Day Money Back Guarantee.';
  const link = AppRoute.RETURN_POLICY;
  const formattedDate = formatDateMST(expirationDate);
  const policyEndDate = `${formattedDate} at 5pm Arizona Time`;

  // Redirect to 7dmbg faq
  const handleRedirect = () => {
    trackEvent(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      daysLeft,
      faqClicked: true,
    });
    datadogRum.addAction(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      daysLeft,
      faqClicked: true,
    });
    NavigateToWindowWithNativeEvents({
      url: link,
      event: 'link',
    });
  };

  const handle7DMBGButtonClick = (e: React.SyntheticEvent, isExpired: boolean) => {
    e.preventDefault();

    trackEvent(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      daysLeft,
      faqClicked: false,
    });
    datadogRum.addAction(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      daysLeft,
      faqClicked: false,
    });

    if (isExpired) {
      NavigateToWindowWithNativeEvents({
        url: link,
        event: 'link',
      });
    } else {
      if (isCarvanaNativeApp()) {
        sendNativeMessage('open_sebastian' as NativeMessageEvent, {});
      } else {
        const chatWindowBtn = document.getElementById('chat-ui-bubble');

        if (chatWindowBtn) {
          chatWindowBtn.click();
        }
      }
    }
  };

  const captureImpression = () => {
    setTimeout(() => {
      trackEvent(AnalyticEvents.viewedOvpCardImpression, {
        cardId,
        cardPosition,
        daysLeft,
      });
      datadogRum.addAction(AnalyticEvents.viewedOvpCardImpression, {
        cardId,
        cardPosition,
        daysLeft,
      });
    }, 1000);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && captureImpression) captureImpression();
    },
  });

  const header = {
    title: <BoldTitle>{title}</BoldTitle>,
  };

  // Hide progress bar and badge until the card is in viewport
  const isInViewport = useInViewport(progressBarBadgeRef, 'progress-bar-badge');
  const isExpired = daysLeft <= 0 && expired;
  return (
    <SevenDayGuaranteeCardWrapper ref={ref}>
      <Card dataQa="seven-day-guarantee" header={header} headerIcon={<MoneyBackIcon />}>
        <ProgressBarAndBadgeWrapper id="progress-bar-badge" ref={progressBarBadgeRef}>
          {isInViewport && (
            <>
              <MoneyBackBadge
                daysLeft={daysLeft}
                isExpired={expired}
                expirationDate={formattedDate}
                isInViewport={isInViewport}
              />
              <MoneyBackProgressBar daysLeft={daysLeft} isExpired={expired} isInViewport={isInViewport} />
            </>
          )}
        </ProgressBarAndBadgeWrapper>
        <ContentWrapper>
          <Content>
            <TextWrapper>
              {isExpired && (
                <InfoText data-testid="7DMBG-info-text-expired">
                  Your 7-day Money Back Guarantee has ended. We&lsquo;re thrilled you&lsquo;re in love with your
                  new-to-you car!
                </InfoText>
              )}
              {!isExpired && (
                <>
                  <InfoText data-testid="7DMBG-info-text-current">
                    Your vehicle has arrived and you have until <BoldText>{policyEndDate}</BoldText> to take it for a
                    spin!
                  </InfoText>
                  <AdditionalInfoText data-testid="additional-info-text">
                    Learn more about our{' '}
                    <CustomLink data-testid="7DMBG-info-link" type="button" onClick={handleRedirect}>
                      {buttonText}
                    </CustomLink>
                  </AdditionalInfoText>
                </>
              )}
            </TextWrapper>
          </Content>
          <Button data-testid="7DMBG-cta-button" size="small" onClick={(e) => handle7DMBGButtonClick(e, isExpired)}>
            {isExpired ? 'LEARN MORE' : 'Chat with us'}
          </Button>
        </ContentWrapper>
      </Card>
    </SevenDayGuaranteeCardWrapper>
  );
};

export default SevenDayGuaranteeCard;
